import React, { useState } from 'react';
import { Box, Collapse, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { CaretDown, CaretUp, Code, CopySimple, PencilSimple } from 'phosphor-react';
import { useNfseEmissao } from '../../../../../../hooks/useNfseEmissao';
import { INfseDpsForm, INfseEmitente } from '../../../../../../types/nfseEmissao';
import { extractApiErrorMessage } from '../../../../../../utils/ErrorUtils';
import { island } from '../../shared/NfseIsland';
import { Banner, C, Card, Eyebrow, IconTile, MONO, NButton, NfseDrawer } from '../../shared/ui';

function Fixo({ rotulo, valor, ajuda }: { rotulo: string; valor: React.ReactNode; ajuda: string }) {
  return (
    <Box>
      <Text fontSize="12px" fontWeight={600} color={C.muted} mb={1}>
        {rotulo}
      </Text>
      <Box
        h="40px"
        px={3}
        display="flex"
        alignItems="center"
        borderRadius="10px"
        bg={C.sunken}
        boxShadow={`inset 0 0 0 1px ${C.line}`}
        fontFamily={MONO}
        fontSize="13px"
        color={C.ink2}
      >
        {valor}
      </Box>
      <Text fontSize="11.5px" color={C.subtle} mt={1}>
        {ajuda}
      </Text>
    </Box>
  );
}

// Campos que a tela preenche sozinha. Ficam à vista para conferência (RF18).
export function TodosOsCampos({
  emitente,
  serie,
  numero,
  reservado,
  ambiente,
}: {
  emitente?: INfseEmitente;
  serie?: string;
  numero?: number | null;
  reservado?: boolean;
  ambiente?: string;
}) {
  const [aberto, setAberto] = useState(false);
  return (
    <Card as="section" p="16px 24px" overflow="visible">
      <Flex
        as="button"
        type="button"
        w="full"
        align="center"
        gap={3}
        textAlign="left"
        aria-expanded={aberto}
        onClick={() => setAberto(!aberto)}
      >
        <IconTile icon={PencilSimple} tone="indigo" size={36} iconSize={17} />
        <Box flex={1}>
          <Text fontSize="14px" fontWeight={700} color={C.ink}>
            Todos os campos da DPS
          </Text>
          <Text fontSize="12.5px" color={C.subtle}>
            O que a tela preenche sozinha. Campos mudados acima ficam marcados como editado e vão
            para a auditoria (RF18).
          </Text>
        </Box>
        {aberto ? <CaretUp size={18} /> : <CaretDown size={18} />}
      </Flex>
      <Collapse in={aberto} animateOpacity>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={5}>
          <Fixo
            rotulo="Série"
            valor={serie ?? '—'}
            ajuda={reservado ? 'Reservada para esta nota' : 'Altere no card do emitente'}
          />
          <Fixo
            rotulo="nDPS"
            valor={numero ?? '—'}
            ajuda={
              reservado
                ? 'Reservado para esta nota (RN05)'
                : 'Altere no card do emitente; fica reservado no envio (RN05)'
            }
          />
          <Fixo
            rotulo="dhEmi"
            valor="na hora do envio"
            ajuda="Com fuso -03:00; nunca no futuro (RN06)"
          />
          <Fixo
            rotulo="tpAmb"
            valor={ambiente === 'producao' ? '1 · Produção' : '2 · Homologação'}
            ajuda="Vem do ambiente do servidor"
          />
          <Fixo
            rotulo="tpEmit"
            valor="1 · Prestador"
            ajuda="Prestador: sem nome e endereço (RN02)"
          />
          <Fixo
            rotulo="cLocEmi"
            valor={emitente?.codigo_ibge ?? '—'}
            ajuda="IBGE do emitente (RN14)"
          />
        </SimpleGrid>
      </Collapse>
    </Card>
  );
}

// XML com tag, atributo e valor em cores diferentes.
function XmlView({ texto }: { texto: string }) {
  const partes: React.ReactNode[] = [];
  const re = /(<\/?|<\?)([\w:]+)|([\w:]+)=("[^"]*")/g;
  let ultimo = 0;
  let m: RegExpExecArray | null = re.exec(texto);
  let k = 0;
  while (m) {
    if (m.index > ultimo) partes.push(texto.slice(ultimo, m.index));
    if (m[2]) {
      partes.push(m[1]);
      partes.push(
        <span key={(k += 1)} style={{ color: '#0077B6' }}>
          {m[2]}
        </span>,
      );
    } else {
      partes.push(
        <span key={(k += 1)} style={{ color: '#7C5CE0' }}>
          {m[3]}
        </span>,
      );
      partes.push('=');
      partes.push(
        <span key={(k += 1)} style={{ color: '#15803D' }}>
          {m[4]}
        </span>,
      );
    }
    ultimo = re.lastIndex;
    m = re.exec(texto);
  }
  partes.push(texto.slice(ultimo));
  return (
    <Box
      as="pre"
      p={4}
      borderRadius="14px"
      bg={C.sunken}
      boxShadow={`inset 0 0 0 1px ${C.line}`}
      fontFamily={MONO}
      fontSize="12px"
      lineHeight="18px"
      color={C.ink2}
      overflowX="auto"
      whiteSpace="pre"
    >
      {partes}
    </Box>
  );
}

// Fora do FormProvider: quem chama passa o getValues do formulário.
export function useXmlDaDps(getValues: () => INfseDpsForm) {
  const { previaXml } = useNfseEmissao();
  const [aberto, setAberto] = useState(false);
  const previa = useMutation({ mutationFn: () => previaXml(getValues()) });
  return {
    abrir: () => {
      setAberto(true);
      previa.mutate();
    },
    drawer: (
      <NfseDrawer
        isOpen={aberto}
        onClose={() => setAberto(false)}
        size="lg"
        icon={Code}
        tone="indigo"
        title="XML da DPS"
        subtitle={previa.data?.id_dps}
        footer={
          <>
            <NButton
              variant="secondary"
              icon={CopySimple}
              isDisabled={!previa.data}
              onClick={async () => {
                await navigator.clipboard.writeText(previa.data?.xml ?? '');
                island.notify('XML copiado', { tone: 'ok' });
              }}
            >
              Copiar
            </NButton>
            <NButton onClick={() => setAberto(false)}>Fechar</NButton>
          </>
        }
      >
        {previa.isPending && <Eyebrow>Montando o XML…</Eyebrow>}
        {previa.isError && (
          <Banner tone="warning" compact>
            {extractApiErrorMessage(previa.error, 'Não foi possível montar o XML da DPS.')}
          </Banner>
        )}
        {previa.data && (
          <>
            <XmlView texto={previa.data.xml} />
            <Text fontSize="12px" color={C.subtle} mt={2.5}>
              Antes da assinatura (RSA-SHA1, C14N, enveloped), com o nº {previa.data.n_dps} que a
              nota usaria agora. O número só é reservado no envio.
            </Text>
          </>
        )}
      </NfseDrawer>
    ),
  };
}
